import { get, set, cloneDeep } from 'lodash-es';
import type { AnnotatedJsonSchema } from '@web-config-app/core';

/**
 * Resolves oneOf discriminators in a schema based on provided data and returns a flattened schema
 *
 * @param dataPath - Dot notation path to the data (e.g., "person.address.street")
 * @param schema - The JSON Schema to process
 * @param data - The data object used to resolve discriminators
 * @returns A new schema with oneOf discriminators resolved and flattened
 */

const resolveSchemaOneOfDiscriminators = (
  dataPath: string,
  schema: AnnotatedJsonSchema,
  data: unknown,
): AnnotatedJsonSchema => {
  if (!dataPath || !schema) return schema;

  // Create a deep copy of the schema to avoid modifying the original
  const resultSchema = cloneDeep(schema);

  const pathParts = dataPath.split('.');
  let currentSchemaPath = '';
  let currentDataPath = '';

  for (let i = 0; i < pathParts.length; i += 1) {
    const part = pathParts[i];

    // Build the data path incrementally
    currentDataPath = currentDataPath ? `${currentDataPath}.${part}` : part;

    // Check if this part is an array index
    const maybeIndex = parseInt(part, 10);
    const isArrayIndex =
      !Number.isNaN(maybeIndex) && String(maybeIndex) === part;

    // Add appropriate separator for schema path
    const pathSeparator = currentSchemaPath ? '.' : '';

    if (isArrayIndex) {
      // For array indices, append 'items'
      currentSchemaPath = `${currentSchemaPath}${pathSeparator}items`;

      // Get the current schema node to check for oneOf combinators
      const currentSchemaNode = get(resultSchema, currentSchemaPath);

      // Handle oneOf with discriminator in array items
      if (
        currentSchemaNode?.oneOf &&
        currentSchemaNode.discriminator?.propertyName
      ) {
        // Get the array at the current data path
        const arrayPath = currentDataPath.substring(
          0,
          currentDataPath.lastIndexOf('.'),
        );
        const arrayData = get(data, arrayPath);

        // Get the specific item in the array
        const itemData = arrayData?.[maybeIndex];

        if (itemData) {
          const discriminatorProp =
            currentSchemaNode.discriminator.propertyName;
          const discriminatorValue = itemData[discriminatorProp];

          // Find the matching schema in oneOf array based on discriminator value
          if (discriminatorValue) {
            const matchedSchema = currentSchemaNode.oneOf.find(
              (oneOfSchema: AnnotatedJsonSchema) => {
                const propPath = `properties.${discriminatorProp}`;
                const enumValues: string[] = get(
                  oneOfSchema,
                  `${propPath}.enum`,
                  [],
                );
                return enumValues && enumValues.includes(discriminatorValue);
              },
            );

            // If we found a matching schema, replace the oneOf with the matched schema
            if (matchedSchema) {
              // Remove the oneOf and discriminator properties
              const newSchema = { ...currentSchemaNode };
              delete newSchema.oneOf;
              delete newSchema.discriminator;

              // Merge the matched schema properties into the parent schema
              Object.assign(newSchema, matchedSchema);

              // Update the resultSchema with the flattened schema
              set(resultSchema, currentSchemaPath, newSchema);
            }
          }
        }
      }
    } else {
      // For regular properties, append 'properties.{part}'
      currentSchemaPath = `${currentSchemaPath}${pathSeparator}properties.${part}`;

      // Get the current schema node
      const currentSchemaNode = get(resultSchema, currentSchemaPath);

      // Handle oneOf with discriminator for object properties
      if (
        currentSchemaNode?.oneOf &&
        currentSchemaNode.discriminator?.propertyName
      ) {
        // Get the object data
        const objectData = get(data, currentDataPath);

        if (objectData) {
          const discriminatorProp =
            currentSchemaNode.discriminator.propertyName;
          const discriminatorValue = objectData[discriminatorProp];

          // Find matching schema based on discriminator value
          if (discriminatorValue) {
            const matchedSchema = currentSchemaNode.oneOf.find(
              (oneOfSchema: AnnotatedJsonSchema) => {
                const propPath = `properties.${discriminatorProp}`;
                const enumValues: string[] = get(
                  oneOfSchema,
                  `${propPath}.enum`,
                  [],
                );
                return enumValues && enumValues.includes(discriminatorValue);
              },
            );

            // If we found a matching schema, replace the oneOf with the matched schema
            if (matchedSchema) {
              // Remove the oneOf and discriminator properties
              const newSchema = { ...currentSchemaNode };
              delete newSchema.oneOf;
              delete newSchema.discriminator;

              // Merge the matched schema properties into the parent schema
              Object.assign(newSchema, matchedSchema);

              // Update the resultSchema with the flattened schema
              set(resultSchema, currentSchemaPath, newSchema);
            }
          }
        }
      }
    }
  }

  return resultSchema;
};

/**
 * A helper function that extracts the schema at a particular data path after resolving any oneOf discriminators
 *
 * @param dataPath - Dot notation path to the data
 * @param schema - The JSON Schema to process
 * @param data - The data object used to resolve discriminators
 * @returns The schema node at the specified path with any oneOf discriminators resolved
 */
export const getResolvedSchemaNodeAtPath = (
  dataPath: string,
  schema: AnnotatedJsonSchema,
  data: unknown,
): AnnotatedJsonSchema | undefined => {
  // First resolve any oneOf discriminators in the schema
  const resolvedSchema = resolveSchemaOneOfDiscriminators(
    dataPath,
    schema,
    data,
  );

  // Convert data path to schema path
  let schemaPath = '';
  const pathParts = dataPath.split('.');

  pathParts.forEach((part) => {
    if (schemaPath) schemaPath += '.';

    const maybeIndex = parseInt(part, 10);
    const isArrayIndex =
      !Number.isNaN(maybeIndex) && String(maybeIndex) === part;

    if (isArrayIndex) {
      schemaPath += 'items';
    } else {
      schemaPath += `properties.${part}`;
    }
  });

  // Return the schema node at the calculated schema path
  return schemaPath ? get(resolvedSchema, schemaPath) : resolvedSchema;
};
